import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Input } from '@/components_deprecated/Input'
import { Popup } from '@/components_deprecated/Popup'
import { Text } from '@/components_deprecated/Text'
import { DetectBlur } from '@/containers/util-containers'
import UserThumbOrInitials from '@/modules/users/UserThumbOrInitials'
import React, { useState } from 'react'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useCompany, useMedia, useUser } from '@/utils/hooks'
import { signOut } from '@/utils/signOut'

const ItemWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  user-select: none;

  span {
    padding-left: 0.5rem;
    flex: 8 0;
  }

  i {
    flex: 2 0;
  }
`
const CompaniesItemWrapper = styled.div`
  display: flex;
  flex-direction: column;
  user-select: none;
`
const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  padding: 0.5rem;
  width: 100%;
  height: 100%;
  border-bottom: 1px solid ${props => props.theme.colors.gray9};

  div:first-child {
    margin-right: 0.5rem;
  }
`
const COMPANY_ITEM_HEIGHT = 48

const Companies = styled.div<{ open: boolean }>`
  display: flex;
  flex-direction: column;
  height: ${props => (props.open ? '100%' : '0px')};
  overflow: hidden;
  max-height: ${COMPANY_ITEM_HEIGHT * 3 + COMPANY_ITEM_HEIGHT / 2}px;
  transform: translate(-1.5rem, 1rem);
  width: calc(100% + 3rem);
  overflow-y: scroll;
  border-top: ${props =>
    props.open ? `1px solid ${props.theme.colors.gray7}` : '0'};
  border-bottom: ${props =>
    props.open ? `1px solid ${props.theme.colors.gray7}` : '0'};
  background-color: ${props => props.theme.colors.white};
`

const Company = styled.div`
  display: flex;
  align-items: center;
  min-height: ${COMPANY_ITEM_HEIGHT}px;
  cursor: pointer;

  padding-left: 1.5rem;
  &:hover {
    background-color: ${props => props.theme.colors.gray9};
  }
`

const CompanySearch = styled(Input)`
  position: sticky;
  border-bottom: 1px solid ${props => props.theme.colors.gray8};
`

const SettingsWrapper = styled.div`
  display: flex;
  height: 100%;
  justify-content: center;
  align-items: center;
  position: relative;
`

export const HeaderMenuUser: React.FC = () => {
  const translations = useTranslate({
    myProfile: 'users.my-profile',
    userSettings: 'users.settings',
    changeCompany: 'common.change-company',
    logOut: 'common.log-out',
  })

  const navigate = useNavigate()

  const [showPopup, setShowPopup] = useState(false)
  const [showCompanies, setShowCompanies] = useState(false)

  const [mobile] = useMedia()
  const user = useUser()
  const company = useCompany()

  const [companySearch, setCompanySearch] = useState('')

  const companies =
    user.companies?.filter(
      otherCompany => otherCompany.shortName !== company.shortName
    ) || []

  const showCompanyFilter = companies.length > 2

  const filteredCompanies =
    companies.filter(company =>
      company.fullName
        .toLocaleLowerCase()
        .includes(companySearch.toLocaleLowerCase())
    ) || companies

  const config = {
    header: function SettingsHeader() {
      return (
        <PopupHeader>
          <UserThumbOrInitials size={60} user={user} />
          <p>{user.fullName}</p>
        </PopupHeader>
      )
    },
    listItems: [
      {
        id: 'my-profile',
        content: () => (
          <ItemWrapper>
            <Icon icon="user" type="solid" size="1rem" />
            <Text>{translations.myProfile}</Text>
          </ItemWrapper>
        ),
        clickable: true,
        onClick: (close: () => void) => {
          void navigate(`/users/${user.id}`)
          close()
        },
      },
      {
        id: 'users.settings',
        content: () => (
          <ItemWrapper>
            <Icon icon="cog" type="solid" size="1rem" />
            <Text>{translations.userSettings}</Text>
          </ItemWrapper>
        ),
        clickable: true,
        onClick: (close: () => void) => {
          void navigate(`/usersettings`)
          close()
        },
      },
      {
        id: 'change-company',
        hide: companies.length < 1,
        clickable: true,
        content: () => (
          <DetectBlur active onClickOutside={() => setShowCompanies(false)}>
            <CompaniesItemWrapper>
              <ItemWrapper>
                <Icon icon="building" type="solid" size="1rem" />
                <Text>{translations.changeCompany}</Text>
              </ItemWrapper>
              <Companies open={showCompanies}>
                {showCompanyFilter && (
                  <CompanySearch
                    onClick={e => {
                      e.stopPropagation()
                    }}
                    value={companySearch}
                    onChange={value => {
                      setCompanySearch(value)
                    }}
                    height="42px"
                    fontSize="14px"
                    borderRadius="0"
                    type="search"
                    padding="1rem 0"
                    noBorders
                    grayed
                  />
                )}
                {filteredCompanies.map(company => (
                  <Company
                    key={company.shortName}
                    onClick={() => {
                      window.location.href = `/c/${company.shortName}/dashboard`
                    }}
                  >
                    {company.fullName}
                  </Company>
                ))}
              </Companies>
            </CompaniesItemWrapper>
          </DetectBlur>
        ),
        onClick() {
          setShowCompanies(!showCompanies)
        },
      },
      {
        id: 'log-out',
        content: () => (
          <ItemWrapper>
            <Icon icon="sign-out" type="solid" size="1rem" />
            <Text>{translations.logOut}</Text>
          </ItemWrapper>
        ),
        clickable: true,
        onClick: signOut,
      },
    ],
  }

  return (
    <DetectBlur
      active={showPopup}
      onClickOutside={() => {
        setShowPopup(!showPopup)
      }}
    >
      <SettingsWrapper>
        <UserThumbOrInitials
          size={58}
          user={user}
          cursor="pointer"
          color="primary"
          fontWeight="600"
          backgroundColor="secondary"
          onClick={() => {
            setShowPopup(!showPopup)
          }}
        />

        <Popup
          show={showPopup}
          renderHeader={config.header}
          renderItems={config.listItems}
          width="250px"
          right={mobile ? '0.5rem' : undefined}
          onClose={() => setShowPopup(false)}
        />
      </SettingsWrapper>
    </DetectBlur>
  )
}
